<!--
 * Path: packages/frontend/src/features/browser/forms/Order.vue
 * Task: Re-order context-aware list entities.
 * Refactoring: Migrated active node highlight and save state to workspace and UI stores.
-->
<template>
  <FormBlockWrapper
    :is-editing="isEditing"
    @update:is-editing="handleToggleEdit"
    :title="field.name"
    :info="field.info"
    :can-edit="canEdit"
    :is-saving="uiStore.loading.save"
    read-class="py-4"
    @save="saveChanges"
    @show-info="$emit('show-info')"
  >
    <template #title-append>
      <v-chip size="x-small" color="blue-grey" variant="flat" class="ms-2 font-weight-bold">GLOBAL</v-chip>
    </template>

    <template #read>
      <div class="order-grid">
        <template v-for="(item, index) in sortedList" :key="item.id">
          <!-- Rank Label (Left Column) -->
          <div class="order-rank d-flex justify-end pe-3">
            <v-chip size="small" :color="item.id === activeEntityId ? 'primary' : 'surface-variant'" class="font-weight-bold elevation-1" label>
              #{{ index + 1 }}
            </v-chip>
          </div>

          <!-- Entity Node (Right Column) -->
          <div class="order-panel min-w-0">
            <EntityPanel
              :entity="item"
              :is-link="true"
              :class="{ 'active-node': item.id === activeEntityId }"
            />
          </div>

          <!-- Vertical Connecting Line (Between Nodes) -->
          <div v-if="index < sortedList.length - 1" class="order-connector d-flex flex-column align-center pt-1 pb-1">
            <div class="connector-line"></div>
            <div class="connector-arrow"></div>
          </div>
        </template>
      </div>

      <div v-if="sortedList.length === 0" class="text-center text-caption text-disabled font-italic py-4">
        No items configured.
      </div>
    </template>

    <template #edit>
      <v-alert type="info" variant="tonal" density="compact" class="mb-4 text-caption">
        Drag and drop the rows to change the global order of all <strong>{{ context }}</strong>.
      </v-alert>

      <DraggableTable
        v-model="editableList"
        :headers="tableHeaders"
      >
        <template #item.displayName="{ item }">
          <div class="d-flex align-center" :class="{ 'text-primary font-weight-bold': item.id === activeEntityId }">
            <v-icon v-if="item.id === activeEntityId" size="small" class="me-2 text-primary">mdi-circle-small</v-icon>
            {{ item.title }}
          </div>
        </template>
      </DraggableTable>
    </template>
  </FormBlockWrapper>
</template>

<script setup lang="ts">
import { ref, computed, inject } from 'vue';
import { useWorkspaceStore } from '@/store/workspace';
import { useUiStore } from '@/store/ui';
import { useAuthStore } from '@/store/auth';
import { useDatastoreStore } from '@/store/datastore';
import { useProjectStore } from '@/store/project';
import { usePermissions } from '@/core/composables/usePermissions';
import { FormContextKey } from '@/core/keys';
import type { Entity, EntityType } from '@/core/types/editor';
import FormBlockWrapper from '@/core/components/forms/FormBlockWrapper.vue';
import DraggableTable, { type TableHeader } from '@/core/components/forms/DraggableTable.vue';
import EntityPanel from '@/core/components/common/EntityPanel.vue';

interface FormField {
  id: string;
  name: string;
  info?: string;
}

const { context, field } = defineProps<{ context: EntityType | string; field: FormField; }>();

const emit = defineEmits<{ (e: 'show-info'): void; }>();

const workspaceStore = useWorkspaceStore();
const uiStore = useUiStore();
const authStore = useAuthStore();
const datastoreStore = useDatastoreStore();
const projectStore = useProjectStore();
const { canEditWorkspace } = usePermissions();
const formContext = inject(FormContextKey, null);

const activeEntityId = computed<string | null>(() => formContext ? formContext.entityId.value : workspaceStore.selectedEntityId);

const isEditing = ref(false);
const canEdit = computed(() => canEditWorkspace.value);

const editableList = ref<Entity[]>([]);

const listForContext = computed<Entity[]>(() => {
  return datastoreStore.allEntities.filter((e: Entity) => e.entityType === context);
});

const sortedList = computed<Entity[]>(() => {
  return [...listForContext.value].sort((a: Entity, b: Entity) => (a.rank || 0) - (b.rank || 0));
});

const tableHeaders: TableHeader[] = [
  { title: 'Item', key: 'displayName', width: '100%' }
];

const handleToggleEdit = (newVal: boolean) => {
  if (!newVal) {
    editableList.value = [];
  } else {
    editableList.value = JSON.parse(JSON.stringify(sortedList.value));
  }
  isEditing.value = newVal;
};

const saveChanges = async () => {
  if (!datastoreStore.data || !projectStore.loadedProject?.value) return;

  uiStore.loading.save = true;
  try {
    const updatedCollection = editableList.value.map((item, index) => {
      item.rank = (index + 1) * 10;
      return item;
    });

    datastoreStore.data[context as string] = updatedCollection;
    datastoreStore.setDirty(true);

    await datastoreStore.saveProjectDatastore(projectStore.loadedProject.value);
    await datastoreStore.fetchProjectDatastore(projectStore.loadedProject.value);

    authStore.showNotification(`Order of ${context} saved successfully.`, 'success');
    isEditing.value = false;
  } catch (error: any) {
    authStore.showNotification(`Failed to save order: ${error.message}`, 'error');
  } finally {
    uiStore.loading.save = false;
  }
};
</script>

<style scoped>
/* Visuelles CSS Grid fuer das vertikale Diagramm */
.order-grid {
  display: grid;
  grid-template-columns: 70px minmax(200px, 320px);
  justify-content: center;
  align-items: center;
}
.order-rank {
  grid-column: 1;
}
.order-panel {
  grid-column: 2;
  z-index: 2;
}

/* Verbindungs-Pfeile zwischen den EntityPanels */
.order-connector {
  grid-column: 2;
  z-index: 1;
}
.connector-line {
  width: 2px;
  height: 16px;
  background-color: rgba(var(--v-theme-on-surface), 0.25);
}
.connector-arrow {
  width: 0;
  height: 0;
  border-left: 5px solid transparent;
  border-right: 5px solid transparent;
  border-top: 6px solid rgba(var(--v-theme-on-surface), 0.25);
}

/* Hervorhebung der aktuellen Node */
:deep(.active-node) {
  cursor: default !important;
  border: 2px solid rgb(var(--v-theme-primary)) !important;
  box-shadow: 0 4px 16px rgba(var(--v-theme-primary), 0.15) !important;
}
</style>
